// Pure calculation logic for the Time Lapse Calculator.
// No React, no side effects, no imports — just math and validation.
//
// v2 (#calculator-redesign) added three scenarios (single shoot, long-term
// project, driving) and a third solve direction (recording time). Everything
// v1 wrote still loads: a params object with only `mode` and the project
// fields resolves to scenario 'project' and computes exactly as before.

const DAY_SECONDS = 24 * 60 * 60;
const MONTH_DAYS = 30.44;
const METRES_PER_MILE = 1609.344;
const FEET_PER_METRE = 3.28084;

// Playback frame-rate options offered across the calculator surfaces.
export const FPS_OPTIONS = [24, 25, 30, 60];

// What is being shot. Decides which inputs are asked for and how the total
// recording time is derived; every scenario ends up at the same core math.
export const SCENARIOS = ['shoot', 'project', 'drive'];

// Which number the user wants back. 'length' → video length (v1 'interval'
// mode), 'interval' → capture interval (v1 'target' mode), 'duration' → how
// long they need to record.
export const SOLVE_FOR_OPTIONS = ['length', 'interval', 'duration'];

// Duration units. minutes/hours belong to the single-shoot scenario; the
// calendar units are the long-term project ones v1 shipped with.
export const DURATION_UNITS = ['minutes', 'hours', 'days', 'weeks', 'months'];
export const CALENDAR_UNITS = ['days', 'weeks', 'months'];

// The speed MotionPace uses for the still stretches when estimating the paced
// length below. MIRRORS `MAX_SPEED_DEFAULT` in ./motionPace.js, which is the
// product default the render engine actually applies. The two are pinned
// together by timelapseCalcPaceCeiling.conformance.test.js — this file stays
// import-free on purpose (it is the one module the prerenderer, the in-app
// dialog and the marketing page all pull in), so the constant is duplicated
// rather than imported, and the test is what stops the copies drifting.
export const PACE_CEILING = 7;

// The in-app capture-plan dialog seeds from this, and saved v1 plans are
// merged over it, so its project-scenario values must not move.
export const DEFAULT_CALCULATOR_STATE = {
  scenario: 'project',
  solveFor: 'length',
  durationValue: 6,
  durationUnit: 'months',
  weekdayMask: [true, true, true, true, true, false, false],
  dailyStartMin: 7 * 60,
  dailyEndMin: 17 * 60,
  is24h: false,
  distanceValue: 30,
  distanceUnit: 'mi',
  speedValue: 45,
  intervalSeconds: 10 * 60,
  targetVideoSeconds: 90,
  fps: 30,
  deadTimeFraction: 0.4,
  mbPerFrame: 5,
  mode: 'interval',
};

// What the public page opens on: a two-hour shoot at one photo every 5 s,
// which answers the query most visitors arrive with ("how long is my clip").
// 7200 s / 5 s = 1,440 frames; at 30 fps that plays for 48 s.
export const PUBLIC_DEFAULT_STATE = {
  ...DEFAULT_CALCULATOR_STATE,
  scenario: 'shoot',
  solveFor: 'length',
  mode: 'interval',
  durationValue: 2,
  durationUnit: 'hours',
  intervalSeconds: 5,
  targetVideoSeconds: 30,
  fps: 30,
  deadTimeFraction: 0.25,
  mbPerFrame: 5,
};

// Seeded the first time someone switches into the driving scenario: a 40-min
// drive at one frame a second, which is the shape of a usable dashcam lapse.
export const DRIVE_DEFAULTS = {
  distanceValue: 30,
  distanceUnit: 'mi',
  speedValue: 45,
  intervalSeconds: 1,
  deadTimeFraction: 0.15,
  targetVideoSeconds: 45,
};

export function countDaysPerWeek(mask) {
  return (mask || []).filter(Boolean).length;
}

// v1 stored `mode`; v2 stores `solveFor`. These two keep the pair in sync so
// an old plan/URL opens on the right control and a new one still writes a
// `mode` an older client could read.
export function solveForToMode(solveFor) {
  return solveFor === 'interval' ? 'target' : 'interval';
}

export function modeToSolveFor(mode) {
  return mode === 'target' ? 'interval' : 'length';
}

function normalizeScenario(scenario) {
  return SCENARIOS.includes(scenario) ? scenario : 'project';
}

function normalizeSolveFor(solveFor, mode) {
  if (SOLVE_FOR_OPTIONS.includes(solveFor)) return solveFor;
  return modeToSolveFor(mode);
}

// Map a UI input snapshot to calculateTimelapse params.
export function calcParamsFromState(state) {
  const s = state || {};
  const scenario = normalizeScenario(s.scenario);
  const solveFor = normalizeSolveFor(s.solveFor, s.mode);
  return {
    scenario,
    solveFor,
    durationValue: s.durationValue,
    durationUnit: s.durationUnit,
    daysPerWeek: countDaysPerWeek(s.weekdayMask),
    dailyStartMin: s.is24h ? 0 : s.dailyStartMin,
    dailyEndMin: s.is24h ? 1440 : s.dailyEndMin,
    distanceValue: s.distanceValue,
    distanceUnit: s.distanceUnit,
    speedValue: s.speedValue,
    intervalSeconds: s.intervalSeconds,
    targetVideoSeconds: s.targetVideoSeconds,
    fps: s.fps,
    deadTimeFraction: s.deadTimeFraction,
    mbPerFrame: s.mbPerFrame,
    mode: solveForToMode(solveFor),
  };
}

// Standard interval "stops" a photographer would actually dial in
// (seconds). Used to snap a computed target-mode interval to something
// buildable on an intervalometer or common camera app. 0.5 s is here for the
// driving case, where a second between frames is already a long way of road.
// Every whole second from 1 to 10 is a stop because short shoots (an
// afternoon, a build day) land in that band and nothing rounder is on offer:
// a 2 hr shoot cut to 30 s at 30 fps wants exactly 8 s, and with the old
// 6 -> 10 jump it snapped to 10 s and answered 24 s, 20% short of the target.
// Any intervalometer or phone app dials whole seconds, so none of these is a
// setting the user cannot actually enter.
export const STANDARD_INTERVAL_STOPS = [
  0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30,
  45, 60, 120, 300, 600, 900,
  1200, 1800, 3600, 7200,
];

// The one-tap interval chips the UI offers, shortest first.
export const INTERVAL_CHIP_SECONDS = [0.5, 1, 2, 5, 10, 30, 60, 300, 600, 1800];

function daysFromUnit(value, unit) {
  if (unit === 'days') return value;
  if (unit === 'weeks') return value * 7;
  if (unit === 'months') return value * MONTH_DAYS;
  return value;
}

function isFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

// Snap a computed interval to the standard stop that keeps the final
// video length CLOSEST to the target. Tie-break: prefer the LONGER
// interval (fewer frames, less storage, less risk of over-shooting).
export function snapIntervalToStandardStop({
  totalShootingSeconds,
  targetVideoSeconds,
  fps,
  deadTimeFraction,
}) {
  const survivorFraction = 1 - deadTimeFraction;
  let best = null;
  for (const stop of STANDARD_INTERVAL_STOPS) {
    const frames = totalShootingSeconds / stop;
    const raw = frames / fps;
    const trimmed = raw * survivorFraction;
    const err = Math.abs(trimmed - targetVideoSeconds);
    if (
      best === null ||
      err < best.err ||
      // Tie-break: prefer longer interval on ~equal error.
      (err === best.err && stop > best.stop)
    ) {
      best = { stop, err, frames, raw, trimmed };
    }
  }
  return best;
}

// Format a seconds duration as "1 hr 12 min", "45 s", "2 min 30 s".
export function formatDuration(seconds) {
  if (!isFiniteNumber(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `${total} s`;
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hrs > 0) {
    if (mins === 0) return `${hrs} hr`;
    return `${hrs} hr ${mins} min`;
  }
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} s`;
}

// "2 hours", "1 hour", "1.5 hours", "6 months": a duration the way the user
// typed it, with the unit singular only when the value is exactly one. The
// answer sentence, the saved-plan card and the default project name all read
// back the raw input, and all three printed "1 hours" before this existed.
export function formatDurationInput(value, unit) {
  if (!DURATION_UNITS.includes(unit)) return `${value} ${unit}`;
  // Only an exact 1 is singular: 0, 1.5 and anything unparseable stay plural.
  const label = Number(value) === 1 ? unit.replace(/s$/, '') : unit;
  return `${value} ${label}`;
}

// Format an interval as "every 10 min" / "every 30 s" / "every 0.5 s".
export function formatInterval(seconds) {
  if (!isFiniteNumber(seconds) || seconds <= 0) return '—';
  if (seconds < 1) {
    // Sub-second stops would round to "0 s" through formatDuration.
    const trimmed = Number(seconds.toFixed(2));
    return `every ${trimmed} s`;
  }
  return `every ${formatDuration(seconds)}`;
}

// How long you stand there with the camera running: "6 hr 40 min", and for
// anything past a day "2 days 6 hr" rather than a 50-hour reading.
export function formatRecordingDuration(seconds) {
  if (!isFiniteNumber(seconds) || seconds < 0) return '—';
  if (seconds < DAY_SECONDS) return formatDuration(seconds);
  const days = Math.floor(seconds / DAY_SECONDS);
  const hrs = Math.round((seconds % DAY_SECONDS) / 3600);
  const dayPart = `${days} day${days === 1 ? '' : 's'}`;
  if (hrs === 0) return dayPart;
  if (hrs === 24) return `${days + 1} days`;
  return `${dayPart} ${hrs} hr`;
}

// How long it takes on a calendar: "3 days", "2 weeks 3 days", "1 month 1 week".
// Months are 30.44 days, the same average the duration units use.
export function formatCalendarDuration(seconds) {
  if (!isFiniteNumber(seconds) || seconds < 0) return '—';
  const days = seconds / DAY_SECONDS;
  if (days < 1) return formatDuration(seconds);
  const whole = Math.round(days);
  if (whole < 14) return `${whole} day${whole === 1 ? '' : 's'}`;
  // Past about a month people count in months, not five-and-a-bit weeks.
  if (whole < 31) {
    const weeks = Math.floor(whole / 7);
    const rest = whole % 7;
    const weekPart = `${weeks} week${weeks === 1 ? '' : 's'}`;
    return rest === 0 ? weekPart : `${weekPart} ${rest} day${rest === 1 ? '' : 's'}`;
  }
  let months = Math.floor(whole / MONTH_DAYS);
  let weeks = Math.round((whole - months * MONTH_DAYS) / 7);
  if (weeks >= 4) { months += 1; weeks = 0; }
  const monthPart = `${months} month${months === 1 ? '' : 's'}`;
  return weeks === 0 ? monthPart : `${monthPart} ${weeks} week${weeks === 1 ? '' : 's'}`;
}

// How a driving lapse reads back at a given ground distance between frames.
// A hint, never an error: 40 m per frame is a legitimate choice on a motorway
// if you want the road to rip past. Thresholds come from what a 30 fps clip
// looks like — under ~10 m the pan is continuous, past ~25 m it strobes.
export function driveFeel(metresPerFrame) {
  if (!isFiniteNumber(metresPerFrame) || metresPerFrame <= 0) return null;
  if (metresPerFrame <= 10) return 'smooth';
  if (metresPerFrame <= 25) return 'brisk';
  return 'jumpy';
}

// Main entry point.
// params: {
//   scenario,                              // 'shoot' | 'project' | 'drive' (default 'project')
//   solveFor,                              // 'length' | 'interval' | 'duration'
//   mode,                                  // legacy alias for solveFor ('interval' | 'target')
//   durationValue, durationUnit,           // e.g. 2, 'hours' / 6, 'months'
//   daysPerWeek,                           // 1..7            (project only)
//   dailyStartMin, dailyEndMin,            // 0..1440         (project only)
//   distanceValue, distanceUnit, speedValue, // drive only
//   intervalSeconds,                       // required unless solving for it
//   targetVideoSeconds,                    // required unless solving for length
//   fps, deadTimeFraction, mbPerFrame,
// }
export function calculateTimelapse(params) {
  const errors = [];
  const p = params || {};

  const scenario = normalizeScenario(p.scenario);
  const solveFor = normalizeSolveFor(p.solveFor, p.mode);
  const mode = solveForToMode(solveFor);

  const durationValue = Number(p.durationValue);
  const durationUnit = p.durationUnit || 'days';
  const daysPerWeek = Number(p.daysPerWeek);
  const dailyStartMin = Number(p.dailyStartMin);
  const dailyEndMin = Number(p.dailyEndMin);
  const distanceValue = Number(p.distanceValue);
  const distanceUnit = p.distanceUnit === 'km' ? 'km' : 'mi';
  const speedValue = Number(p.speedValue);
  const fps = Number(p.fps);
  const deadTimeFraction = Number(p.deadTimeFraction);
  const mbPerFrame = Number(p.mbPerFrame);

  // A single shoot measured in calendar units is still a valid question
  // ("I am leaving the camera out for 3 days"), so it runs the project math
  // with a round-the-clock window instead of erroring.
  const durationIsCalendar = CALENDAR_UNITS.includes(durationUnit);
  const usesProjectSchedule = scenario === 'project' || (scenario === 'shoot' && durationIsCalendar);
  const needsDuration = scenario !== 'drive' && solveFor !== 'duration';

  if (needsDuration) {
    if (!isFiniteNumber(durationValue) || durationValue <= 0) {
      errors.push({ field: 'durationValue', message: 'Duration must be greater than zero.' });
    }
    const allowedUnits = scenario === 'shoot' ? DURATION_UNITS : CALENDAR_UNITS;
    if (!allowedUnits.includes(durationUnit)) {
      errors.push({
        field: 'durationUnit',
        message: scenario === 'shoot'
          ? 'Duration unit must be minutes, hours, days, weeks, or months.'
          : 'Duration unit must be days, weeks, or months.',
      });
    }
  }

  // The daily window: a full 24h day is represented as start=0, end=1440.
  const is24h = dailyStartMin === 0 && dailyEndMin === 1440;
  // Only the long-term project exposes a weekday mask and a daily window, so
  // only it has them to validate. A single shoot measured in calendar days
  // shares the project MATH below but always runs 7 days a week, round the
  // clock, so it has no schedule of its own to get wrong.
  if (scenario === 'project') {
    if (!isFiniteNumber(daysPerWeek) || daysPerWeek < 1 || daysPerWeek > 7) {
      errors.push({ field: 'daysPerWeek', message: 'Shooting days per week must be between 1 and 7.' });
    }
    if (!is24h) {
      if (
        !isFiniteNumber(dailyStartMin) ||
        !isFiniteNumber(dailyEndMin) ||
        dailyStartMin < 0 ||
        dailyEndMin > 1440 ||
        dailyEndMin <= dailyStartMin
      ) {
        errors.push({ field: 'dailyWindow', message: 'Daily end time must be after the start time.' });
      }
    }
  }

  if (scenario === 'drive') {
    if (!isFiniteNumber(distanceValue) || distanceValue <= 0) {
      errors.push({ field: 'distanceValue', message: 'Distance must be greater than zero.' });
    }
    if (!isFiniteNumber(speedValue) || speedValue <= 0) {
      errors.push({ field: 'speedValue', message: 'Average speed must be greater than zero.' });
    }
  }

  if (!isFiniteNumber(fps) || fps <= 0) {
    errors.push({ field: 'fps', message: 'Playback FPS must be positive.' });
  }
  if (!isFiniteNumber(deadTimeFraction) || deadTimeFraction < 0 || deadTimeFraction > 0.9) {
    errors.push({ field: 'deadTimeFraction', message: 'Still footage must be between 0% and 90%.' });
  }
  if (!isFiniteNumber(mbPerFrame) || mbPerFrame <= 0) {
    errors.push({ field: 'mbPerFrame', message: 'Storage per frame must be positive.' });
  }

  const intervalSecondsInput = Number(p.intervalSeconds);
  const targetVideoSeconds = Number(p.targetVideoSeconds);
  if (solveFor !== 'interval') {
    if (!isFiniteNumber(intervalSecondsInput) || intervalSecondsInput <= 0) {
      errors.push({ field: 'intervalSeconds', message: 'Capture interval must be greater than zero.' });
    }
  }
  if (solveFor !== 'length') {
    if (!isFiniteNumber(targetVideoSeconds) || targetVideoSeconds <= 0) {
      errors.push({ field: 'targetVideoSeconds', message: 'Target video length must be greater than zero.' });
    }
  }

  // Early exit if we can't safely derive anything.
  if (errors.length > 0) {
    return { ok: false, errors };
  }

  // ----- Resolve the scenario into a recording-time base -----
  let durationDays;
  let shootingSecondsPerDay;
  let totalShootingDays;
  let totalShootingSeconds;
  let driveSeconds = null;

  if (scenario === 'drive') {
    // Unit-agnostic: miles ÷ mph and km ÷ km/h are the same ratio in hours.
    driveSeconds = (distanceValue / speedValue) * 3600;
    totalShootingSeconds = driveSeconds;
    shootingSecondsPerDay = driveSeconds;
    durationDays = driveSeconds / DAY_SECONDS;
    totalShootingDays = durationDays;
  } else if (usesProjectSchedule) {
    const perWeek = scenario === 'project' ? daysPerWeek : 7;
    durationDays = solveFor === 'duration' ? 0 : daysFromUnit(durationValue, durationUnit);
    shootingSecondsPerDay = scenario === 'project'
      ? (is24h ? DAY_SECONDS : (dailyEndMin - dailyStartMin) * 60)
      : DAY_SECONDS;
    totalShootingDays = (durationDays * perWeek) / 7;
    totalShootingSeconds = totalShootingDays * shootingSecondsPerDay;
  } else {
    // Single shoot in minutes or hours: one uninterrupted session.
    totalShootingSeconds = durationUnit === 'hours' ? durationValue * 3600 : durationValue * 60;
    shootingSecondsPerDay = totalShootingSeconds;
    durationDays = totalShootingSeconds / DAY_SECONDS;
    totalShootingDays = durationDays;
  }

  // An interval longer than the daily window means at most one frame a day,
  // which is almost never intended and makes the numbers below absurd. Only
  // the scheduled project case can hit it — elsewhere the frame-count floor
  // below catches the same mistake with a clearer message.
  if (solveFor === 'length' && scenario === 'project' && intervalSecondsInput > shootingSecondsPerDay) {
    return {
      ok: false,
      errors: [{
        field: 'intervalSeconds',
        message: 'Interval is longer than your daily shooting window. Increase the window or shorten the interval.',
      }],
    };
  }

  const survivorFraction = 1 - deadTimeFraction;
  let intervalSeconds = intervalSecondsInput;
  let recommendedIntervalSeconds = null;
  let snappedRawFrames = null;
  let exactIntervalSeconds = null;
  let targetHitVideoSeconds = null;
  let requiredRecordingSeconds = null;

  if (solveFor === 'interval') {
    // Required RAW frames such that after trimming we hit the target.
    const requiredRawFrames = (targetVideoSeconds * fps) / survivorFraction;
    if (requiredRawFrames < 2) {
      return {
        ok: false,
        errors: [{
          field: 'targetVideoSeconds',
          message: 'Target video length is too short for a meaningful timelapse (need at least 2 frames of raw footage).',
        }],
      };
    }
    exactIntervalSeconds = totalShootingSeconds / requiredRawFrames;
    intervalSeconds = exactIntervalSeconds;

    const snapped = snapIntervalToStandardStop({
      totalShootingSeconds,
      targetVideoSeconds,
      fps,
      deadTimeFraction,
    });
    recommendedIntervalSeconds = snapped.stop;
    snappedRawFrames = Math.round(snapped.frames);
    targetHitVideoSeconds = snapped.trimmed;
  } else if (solveFor === 'duration') {
    // How long must the camera run to fill the target at this interval?
    const requiredRawFrames = (targetVideoSeconds * fps) / survivorFraction;
    if (requiredRawFrames < 2) {
      return {
        ok: false,
        errors: [{
          field: 'targetVideoSeconds',
          message: 'Target video length is too short for a meaningful timelapse (need at least 2 frames of raw footage).',
        }],
      };
    }
    requiredRecordingSeconds = requiredRawFrames * intervalSeconds;
    totalShootingSeconds = requiredRecordingSeconds;
    totalShootingDays = requiredRecordingSeconds / shootingSecondsPerDay;
    durationDays = scenario === 'project' && daysPerWeek > 0
      ? (totalShootingDays * 7) / daysPerWeek
      : totalShootingSeconds / DAY_SECONDS;
  }

  const frameCount = Math.round(totalShootingSeconds / intervalSeconds);
  if (frameCount < 2) {
    return {
      ok: false,
      errors: [{
        field: 'frameCount',
        message: 'These settings produce fewer than 2 frames. Try a shorter interval or a longer recording time.',
      }],
    };
  }

  const rawVideoSeconds = frameCount / fps;
  const trimmedVideoSeconds = rawVideoSeconds * survivorFraction;
  // MotionPace plays the moving footage at 1x and the still footage at the
  // ceiling, so the still share costs 1/PACE_CEILING of its running time
  // instead of being cut outright.
  const pacedVideoSeconds = rawVideoSeconds * (survivorFraction + deadTimeFraction / PACE_CEILING);
  const speedup = rawVideoSeconds > 0 ? totalShootingSeconds / rawVideoSeconds : 0;
  const storageGB = (frameCount * mbPerFrame) / 1024;
  const framesPerDay = shootingSecondsPerDay / intervalSeconds;

  const result = {
    ok: true,
    scenario,
    solveFor,
    mode,
    // Echo-back computed durations for the UI.
    durationDays,
    shootingSecondsPerDay,
    totalShootingDays,
    totalShootingSeconds,
    intervalSeconds,
    frameCount,
    rawVideoSeconds,
    trimmedVideoSeconds,
    pacedVideoSeconds,
    speedup,
    storageGB,
    framesPerDay,
  };

  if (solveFor === 'interval') {
    const snappedRaw = snappedRawFrames / fps;
    result.exactIntervalSeconds = exactIntervalSeconds;
    result.recommendedIntervalSeconds = recommendedIntervalSeconds;
    result.snappedFrameCount = snappedRawFrames;
    result.snappedRawVideoSeconds = snappedRaw;
    result.snappedTrimmedVideoSeconds = targetHitVideoSeconds;
    result.snappedPacedVideoSeconds = snappedRaw * (survivorFraction + deadTimeFraction / PACE_CEILING);
  }

  if (solveFor === 'duration') {
    result.requiredRecordingSeconds = requiredRecordingSeconds;
    result.requiredShootingDays = totalShootingDays;
    // Calendar time stretches the shooting days back out over the weeks the
    // crew is not on site: 5-day weeks mean 10 shooting days take 14.
    result.requiredCalendarDays = scenario === 'project' && daysPerWeek > 0
      ? (totalShootingDays * 7) / daysPerWeek
      : totalShootingDays;
  }

  if (scenario === 'drive') {
    const metresPerSecond = distanceUnit === 'km'
      ? (speedValue * 1000) / 3600
      : (speedValue * METRES_PER_MILE) / 3600;
    const metresPerFrame = metresPerSecond * intervalSeconds;
    result.driveSeconds = driveSeconds;
    result.distanceUnit = distanceUnit;
    result.metresPerFrame = metresPerFrame;
    result.feetPerFrame = metresPerFrame * FEET_PER_METRE;
    // Frames per mile (or km): seconds to cover one unit ÷ the interval.
    result.framesPerDistanceUnit = 3600 / (speedValue * intervalSeconds);
    result.driveFeel = driveFeel(metresPerFrame);
  }

  return result;
}

// Collapse a raw result into the display-facing stat set. When solving for the
// interval the user shoots at the SNAPPED interval, so the stat grid must
// describe that plan (not the exact, undialable interval). Returns null for
// invalid input.
export function deriveDisplayStats(result, state) {
  if (!result || !result.ok) return null;
  const solveFor = normalizeSolveFor(state && state.solveFor, state && state.mode);
  if (solveFor !== 'interval') return result;
  const frameCount = result.snappedFrameCount;
  const rawVideoSeconds = result.snappedRawVideoSeconds;
  return {
    ...result,
    intervalSeconds: result.recommendedIntervalSeconds,
    frameCount,
    rawVideoSeconds,
    trimmedVideoSeconds: result.snappedTrimmedVideoSeconds,
    pacedVideoSeconds: result.snappedPacedVideoSeconds,
    speedup: rawVideoSeconds > 0 ? result.totalShootingSeconds / rawVideoSeconds : 0,
    storageGB: (frameCount * Number(state.mbPerFrame)) / 1024,
    framesPerDay: result.shootingSecondsPerDay / result.recommendedIntervalSeconds,
    framesPerDistanceUnit: result.scenario === 'drive'
      ? 3600 / (Number(state.speedValue) * result.recommendedIntervalSeconds)
      : result.framesPerDistanceUnit,
  };
}

// The patch to apply when the user switches scenario. Shared fields (fps,
// still footage, storage) are deliberately carried across; only the fields
// the new scenario cannot express are rewritten:
//   - a single shoot has no weeks or months and a long-term project has no
//     minutes or hours, so a duration in a unit the destination cannot show is
//     replaced by that scenario's DEFAULT duration rather than converted;
//   - driving has no "how long do I record for" question (the drive length is
//     an input), so that solve direction falls back to video length.
//
// Seeding beats converting because the arithmetic produced plans nobody asked
// for: 2 hours became "1 days" (a one-day long-term project shooting 7 hr 8 min)
// and 6 months became "183 days" for a single shoot. `days` is the one unit both
// scenarios can express, so it is carried across untouched.
//
// A seeded duration brings its scenario's DEFAULT INTERVAL with it, because
// the two are a matched pair: 2 hours goes with 5 s, 6 months with 10 min.
// Seeding one and carrying the other over collapses the answer — 2 hours at
// one photo every 10 min is 12 frames, which plays for 0 s, and 6 months at
// one photo every 5 s is 939k frames and ~4.5 TB. Whenever the duration
// survives the switch the interval survives with it, untouched.
//
// That pairing only holds while the DURATION IS AN INPUT. Solving for recording
// time ("how long do I need to record?") makes the duration the answer — the
// solver derives it from the target and the interval and the form hides the
// duration control — so there is no new duration scale for the interval to be
// stale against. The interval is the live input in that direction, and resetting
// it to the scenario default silently rewrites the user's answer (a hand-set
// 30 s becoming 5 s moves "1 d 13 h 30 m" to "6 h 15 m"). So the duration is
// still re-seeded (the stale unit has no control in the destination scenario and
// would strand the selector the moment the user switches direction back), but
// the interval is left alone.
export function scenarioSwitchPatch(state, nextScenario) {
  const scenario = normalizeScenario(nextScenario);
  const patch = { scenario };
  const unit = state.durationUnit;
  const solveFor = normalizeSolveFor(state.solveFor, state.mode);
  // Solving for the interval leaves it a live pairing too: the interval shown
  // is computed, but the value seeded here is what the user gets back when they
  // switch the solve direction, so it must still match the seeded duration.
  const durationIsInput = solveFor !== 'duration';
  if (scenario === 'shoot' && (unit === 'weeks' || unit === 'months')) {
    patch.durationUnit = PUBLIC_DEFAULT_STATE.durationUnit;
    patch.durationValue = PUBLIC_DEFAULT_STATE.durationValue;
    if (durationIsInput) patch.intervalSeconds = PUBLIC_DEFAULT_STATE.intervalSeconds;
  }
  if (scenario === 'project' && (unit === 'minutes' || unit === 'hours')) {
    patch.durationUnit = DEFAULT_CALCULATOR_STATE.durationUnit;
    patch.durationValue = DEFAULT_CALCULATOR_STATE.durationValue;
    if (durationIsInput) patch.intervalSeconds = DEFAULT_CALCULATOR_STATE.intervalSeconds;
  }
  if (scenario === 'drive' && solveFor === 'duration') {
    patch.solveFor = 'length';
    patch.mode = solveForToMode('length');
  }
  return patch;
}
